<template>
  <div class="row g-3" data-testid="forward-sales-materials">
    <div
      v-for="material in materials"
      :key="material.key"
      class="col-12 col-md-4"
    >
      <a
        :href="material.url"
        target="_blank"
        rel="noopener noreferrer"
        class="material-tile d-block h-100 border rounded-3 p-3 text-decoration-none"
        :data-testid="`forward-sales-material-${material.key}`"
      >
        <div class="d-flex align-items-center gap-2 fw-semibold mb-2">
          <FontAwesomeIcon :icon="material.icon" class="text-primary" />
          <span>{{ material.title }}</span>
          <span
            v-if="material.badge"
            class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis fw-normal"
            >{{ material.badge }}</span
          >
        </div>
        <ul class="text-body-tertiary fs-9 mb-0 ps-3">
          <li v-for="line in material.lines" :key="line">{{ line }}</li>
        </ul>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { FORWARD_OUTLOOK_LAST_UPDATED, FORWARD_OUTLOOK_URL } from "@/constants";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * The forward sales onboarding materials of the v1 strategy page, as tiles: the market outlook, the
 * Forward Advantage trading strategy, and the legal framework of forward sales. The text introducing
 * them is the caller's (`ForwardSalesIntroComponent`, or the small-producer panel's own).
 *
 * The strategy exists in Estonian, Lithuanian and Latvian besides English, the framework in Swedish
 * too. As in v1 the company's country picks the version; without one (several companies open) the
 * interface language does, and anything else gets the English one.
 */
const props = withDefaults(
  defineProps<{
    country?: string | null;
  }>(),
  { country: null }
);

type MaterialVersion = "et" | "lt" | "lv" | "sv" | "en";

const VERSION_BY_COUNTRY: Record<string, MaterialVersion> = {
  EE: "et",
  LT: "lt",
  LV: "lv",
  SE: "sv",
};

const STRATEGY_BASE_URL =
  "https://soldera-public-statics.s3.eu-west-1.amazonaws.com/instructions";

const VERSIONED_URLS: Record<
  MaterialVersion,
  { strategy: string; framework: string }
> = {
  et: {
    strategy: `${STRATEGY_BASE_URL}/ee_forward_advantage_v1.pdf`,
    framework: "https://www.soldera.org/et/tingimused/kauplemisteenuste-lisa",
  },
  lt: {
    strategy: `${STRATEGY_BASE_URL}/lt_forward_advantage_v1.pdf`,
    framework: "https://www.soldera.org/lt/salygos/prekybos-paslaugu-priedas",
  },
  lv: {
    strategy: `${STRATEGY_BASE_URL}/lv_forward_advantage_v1.pdf`,
    framework:
      "https://www.soldera.org/lv/noteikumi/tirdzniecibas-pakalpojumu-pielikums",
  },
  // No Swedish strategy exists, so it is the English one.
  sv: {
    strategy: `${STRATEGY_BASE_URL}/en_forward_advantage_v1.pdf`,
    framework: "https://www.soldera.org/sv/villkor/tillagg-for-handelstjanster",
  },
  en: {
    strategy: `${STRATEGY_BASE_URL}/en_forward_advantage_v1.pdf`,
    framework: "https://www.soldera.org/terms/trading-services",
  },
};

const { t, locale } = useI18n();

const versionedUrls = computed(() => {
  const version = props.country
    ? (VERSION_BY_COUNTRY[props.country.toUpperCase()] ?? "en")
    : (locale.value as MaterialVersion);
  return VERSIONED_URLS[version] ?? VERSIONED_URLS.en;
});

const materials = computed(() => [
  {
    key: "outlook",
    url: FORWARD_OUTLOOK_URL,
    icon: "fa-solid fa-file-pdf",
    title: t("market_insights.forward_outlook.title"),
    badge: FORWARD_OUTLOOK_LAST_UPDATED,
    lines: [
      t("production_strategy.forward_materials.outlook_info_1"),
      t("production_strategy.forward_materials.outlook_info_2"),
    ],
  },
  {
    key: "strategy",
    url: versionedUrls.value.strategy,
    icon: "fa-solid fa-file-pdf",
    title: t("production_strategy.forward_materials.strategy_title"),
    badge: null,
    lines: [t("production_strategy.forward_materials.strategy_info")],
  },
  {
    key: "framework",
    url: versionedUrls.value.framework,
    icon: "fa-solid fa-link",
    title: t("production_strategy.forward_materials.framework_title"),
    badge: null,
    lines: [t("production_strategy.forward_materials.framework_info")],
  },
]);
</script>

<style scoped lang="scss">
.material-tile {
  color: inherit;
  background-color: var(--phoenix-body-highlight-bg);
  transition: border-color 0.15s ease-in-out;

  &:hover,
  &:focus-visible {
    border-color: var(--phoenix-primary) !important;
  }
}
</style>
